import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { useCategoryItems, useCreateItem } from "../hooks/useDropdowns";
import { slugifyValue, type CategoryMeta } from "../types";
import { getMetadataSchema } from "../metadataSchemas";
import { MetadataFields } from "./MetadataFields";

interface AddItemModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  category: CategoryMeta;
  defaultSortOrder: number;
}

export function AddItemModal({
  open,
  onOpenChange,
  category,
  defaultSortOrder,
}: AddItemModalProps) {
  const create = useCreateItem(category.category);
  const { data: parentItems = [] } = useCategoryItems(
    category.parentCategory ?? undefined,
  );

  const [label, setLabel] = useState("");
  const [value, setValue] = useState("");
  const [valueDirty, setValueDirty] = useState(false);
  const [description, setDescription] = useState("");
  const [sortOrder, setSortOrder] = useState(defaultSortOrder);
  const [isActive, setIsActive] = useState(true);
  const [parentValue, setParentValue] = useState("");
  const [metadata, setMetadata] = useState<Record<string, unknown>>({});
  const [error, setError] = useState<string | null>(null);

  const metadataSchema = getMetadataSchema(category.category);

  useEffect(() => {
    if (open) {
      setLabel("");
      setValue("");
      setValueDirty(false);
      setDescription("");
      setSortOrder(defaultSortOrder);
      setIsActive(true);
      setParentValue("");
      setMetadata({});
      setError(null);
    }
  }, [open, defaultSortOrder]);

  function handleLabelChange(next: string) {
    setLabel(next);
    if (!valueDirty) setValue(slugifyValue(next));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!label.trim() || !value.trim()) {
      setError("Label and value are required");
      return;
    }
    if (category.parentCategory && !parentValue) {
      setError("Parent value is required for dependent dropdowns");
      return;
    }
    try {
      await create.mutateAsync({
        label: label.trim(),
        value: value.trim(),
        description: description.trim() || null,
        sortOrder,
        isActive,
        parentValue: category.parentCategory ? parentValue : null,
        metadata: metadataSchema ? metadata : undefined,
      });
      onOpenChange(false);
    } catch (err) {
      setError((err as Error).message ?? "Failed to create item");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Value — {category.name}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="label">Label</Label>
            <Input
              id="label"
              value={label}
              onChange={(e) => handleLabelChange(e.target.value)}
              placeholder="Display label"
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="value">Value (code)</Label>
            <Input
              id="value"
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                setValueDirty(true);
              }}
              placeholder="auto_generated_from_label"
              className="font-mono text-xs"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Input
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional"
            />
          </div>

          {metadataSchema && (
            <MetadataFields
              category={category.category}
              value={metadata}
              onChange={setMetadata}
            />
          )}

          {category.parentCategory && (
            <div className="space-y-2">
              <Label htmlFor="parentValue">
                Parent Value ({category.parentCategory})
              </Label>
              <Select
                id="parentValue"
                value={parentValue}
                onChange={(e) => setParentValue(e.target.value)}
              >
                <option value="">— Select parent —</option>
                {parentItems
                  .filter((p) => p.isActive)
                  .map((p) => (
                    <option key={p.id} value={p.value}>
                      {p.label}
                    </option>
                  ))}
              </Select>
              <p className="text-[11px] text-muted-foreground">
                This value only appears when the parent dropdown is set to the
                selected option.
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="sortOrder">Sort Order</Label>
              <Input
                id="sortOrder"
                type="number"
                min={0}
                value={sortOrder}
                onChange={(e) => setSortOrder(Number(e.target.value) || 0)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="active">Status</Label>
              <div className="flex h-9 items-center gap-2">
                <input
                  id="active"
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="h-4 w-4 rounded border-border"
                />
                <span className="text-sm">{isActive ? "Active" : "Inactive"}</span>
              </div>
            </div>
          </div>

          {error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
              {error}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending && (
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
              )}
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
